import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '@/store/notification-store'
import { notificationsService } from '@/services/notifications-service'
import type { AppNotification } from '@/types'

/* ── Inline SVG icons ── */
const BellIcon = () => (
  <svg width="18" height="18" fill="none" stroke="#64748B" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

/* ── Time formatting ── */
function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const seconds = Math.floor((now - then) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/* ── Type-based icon color ── */
function typeColor(type: string): string {
  switch (type) {
    case 'estimate_approved': return '#10b981'     // green
    case 'estimate_rejected': return '#ef4444'     // red
    case 'estimate_submitted_for_approval': return '#8b5cf6' // purple
    case 'proposal_signed': return '#3b82f6'       // blue
    default: return '#6b7280'
  }
}

function typeIcon(type: string) {
  const color = typeColor(type)
  switch (type) {
    case 'estimate_approved':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )
    case 'estimate_rejected':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      )
    case 'estimate_submitted_for_approval':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M12 8v4l3 3" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      )
    case 'proposal_signed':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M9 15l2 2 4-4" />
        </svg>
      )
    default:
      return <BellIcon />
  }
}

/* ── Mini Confetti (contained within notification item) ── */
function MiniConfetti() {
  const colors = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316']
  const pieces = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    duration: 0.8 + Math.random() * 0.8,
    color: colors[i % colors.length],
    size: 4 + Math.random() * 3,
    drift: (Math.random() - 0.5) * 40,
    rotation: Math.random() * 360,
  }))

  return (
    <div className="notif-confetti">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="notif-confetti-piece"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 1.4}px`,
            backgroundColor: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            ['--drift' as string]: `${p.drift}px`,
            ['--rotation' as string]: `${p.rotation}deg`,
          }}
        />
      ))}
    </div>
  )
}

/* ── Notification Item ── */
function NotificationItem({
  notification,
  onNavigate,
  onDismiss,
}: {
  notification: AppNotification
  onNavigate: (n: AppNotification) => void
  onDismiss: (e: React.MouseEvent, id: string) => void
}) {
  const isSigned = notification.type === 'proposal_signed'

  return (
    <div
      className={`notif-item${notification.isRead ? '' : ' unread'}${isSigned ? ' signed' : ''}`}
      onClick={() => onNavigate(notification)}
    >
      {isSigned && !notification.isRead && <MiniConfetti />}
      <div className="notif-item-icon" style={{ backgroundColor: `${typeColor(notification.type)}18` }}>
        {typeIcon(notification.type)}
      </div>
      <div className="notif-item-content">
        <p className="notif-item-title">{notification.title}</p>
        <p className="notif-item-message">{notification.message}</p>
        <span className="notif-item-time">{timeAgo(notification.createdAt)}</span>
      </div>
      <button
        className="notif-item-dismiss"
        onClick={(e) => onDismiss(e, notification.id)}
        title="Dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  )
}

/* ── Main Dropdown ── */
export function NotificationDropdown() {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    isOpen,
    toggleOpen,
    setOpen,
    optimisticMarkAsRead,
    optimisticDismiss,
    optimisticMarkAllAsRead,
  } = useNotificationStore()

  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen, setOpen])

  const handleNavigate = useCallback(
    (n: AppNotification) => {
      if (!n.isRead) {
        optimisticMarkAsRead(n.id)
        notificationsService.markAsRead(n.id).catch(() => {})
      }
      setOpen(false)
      navigate(n.actionUrl)
    },
    [navigate, optimisticMarkAsRead, setOpen],
  )

  const handleDismiss = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      optimisticDismiss(id)
      notificationsService.dismiss(id).catch(() => {})
    },
    [optimisticDismiss],
  )

  const handleMarkAllAsRead = useCallback(() => {
    optimisticMarkAllAsRead()
    notificationsService.markAllAsRead().catch(() => {})
  }, [optimisticMarkAllAsRead])

  return (
    <div className="notif-dropdown" ref={dropdownRef}>
      <button className="notif" onClick={toggleOpen}>
        <BellIcon />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllAsRead}>Mark all as read</button>
            )}
          </div>

          <div className="notif-panel-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onNavigate={handleNavigate}
                  onDismiss={handleDismiss}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
